import { useCallback, useContext, useMemo } from "react";
import type { IntlShape } from "react-intl-original";
import { useIntl } from "react-intl-original";
import { RootContext } from "../../store";
import {
  getTerminologyFromSettings,
  getTerminologyResourceLabel,
  resolveTerminologyMessage,
  TerminologyResourceForm,
} from "./orgTerminology";
import { useLang } from "./Streami18n";

/**
 * Intl helpers that apply per-organization terminology from
 * `organization.settings.terminology` when present.
 */
export const useOrgIntl = () => {
  const intl = useIntl();
  const lang = useLang();
  const { company: companyStore } = useContext(RootContext);
  const terminology = useMemo(
    () => getTerminologyFromSettings(companyStore.settings),
    [companyStore.settings]
  );

  const formatMessage = useCallback(
    (
      descriptor: Parameters<IntlShape["formatMessage"]>[0],
      values?: Parameters<IntlShape["formatMessage"]>[1],
      opts?: Parameters<IntlShape["formatMessage"]>[2]
    ) => {
      const result = intl.formatMessage(descriptor, values, opts);
      const id =
        typeof descriptor === "object" && descriptor && "id" in descriptor
          ? descriptor.id
          : undefined;
      if (!id || typeof result !== "string") {
        return result;
      }
      const override = resolveTerminologyMessage(id, result, terminology, lang);
      return override ?? result;
    },
    [intl, lang, terminology]
  ) as IntlShape["formatMessage"];

  const getResourceLabel = useCallback(
    (resourceKey: string, form: TerminologyResourceForm = "singular") =>
      getTerminologyResourceLabel(terminology, resourceKey, form, lang),
    [lang, terminology]
  );

  return useMemo(
    () => ({
      ...intl,
      formatMessage,
      getResourceLabel,
      terminology,
    }),
    [intl, formatMessage, getResourceLabel, terminology]
  );
};
